import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Download, Upload, CheckCircle, AlertTriangle } from 'lucide-react';

import GenericFormDrawer from '../../components/GenericFormDrawer';
import TextInput from '../../components/ui/TextInput';
import DateTimePicker from '../../components/ui/DateTimePicker';
import GenericSelect from '../../components/ui/GenericSelect';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  executeHistoryImport, getHistoryImportTemplate, getRatingScales, validateHistoryImport,
} from '../../services/performanceHistoryImportService';
import { ISSUE_PREVIEW_LIMIT, KINDS, canCommit, metaReady, summaryItems } from './config';

// PM analitik P6 — geçmiş dönem içe aktarma çekmecesi (GenericFormDrawer kabuğu; modal yok). Akış: dönem bilgileri (yeni
// dönemde ad, tarih, sonuç türü, ölçek) → şablon indir → dosya seç (otomatik doğrulama; satır hataları ve uyarılar) → İçe Aktar
// → ConfirmDialog onayı → kayıt (dosya yeniden yüklenir, sunucu doğrulamayı baştan koşar). Dosyada hatalı satır varsa kayıt
// yapılamaz (hepsi ya da hiçbiri). Yeniden yüklemede dosya dönemin TAM listesidir: eklenen/güncellenen/çıkarılan önizlenir.
const Section = styled.h4`
  margin: 16px 0 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldRow = styled.div`
  margin-bottom: 12px;
`;

const Inline = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const Hint = styled.p`
  margin: 0 0 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.5;
`;

const Notice = styled.div`
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border-left: 3px solid ${({ theme, $tone }) => ($tone === 'danger' ? theme.colors.auxiliar.danger
    : $tone === 'success' ? theme.colors.secondary.main : theme.colors.auxiliar.warning)};
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
`;

const Stat = styled.div`
  padding: 8px 10px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgColor};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme, $tone }) => ($tone === 'danger' ? theme.colors.auxiliar.danger : theme.colors.text.primary)};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const IssueRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 6px 10px;
  margin-bottom: 4px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.bgColor};
  border-left: 3px solid ${({ theme, $error }) => ($error ? theme.colors.auxiliar.danger : theme.colors.auxiliar.warning)};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RowNo = styled.span`
  flex-shrink: 0;
  min-width: 64px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const Muted = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const EMPTY_META = { name: '', startDate: '', endDate: '', resultKind: 'level', ratingScaleId: '' };

function download(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function HistoryImportDrawer({ open, onClose, period, onImported }) {
  const { t } = useTranslation();
  const reupload = !!period;
  const [meta, setMeta] = useState(EMPTY_META);
  const [scales, setScales] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setMeta(EMPTY_META);
    setFile(null);
    setPreview(null);
    setStale(false);
    setConfirm(false);
    if (!reupload) {
      getRatingScales().then((res) => {
        const list = res?.data?.data;
        setScales((Array.isArray(list) ? list : (list?.data || [])).map((s) => ({ id: s.id, name: s.name })));
      });
    }
  }, [open, reupload]);

  const requestMeta = useCallback(() => (reupload
    ? { periodId: period.periodId }
    : {
      name: meta.name.trim(), startDate: meta.startDate, endDate: meta.endDate, resultKind: meta.resultKind,
      ratingScaleId: meta.resultKind === 'level' ? meta.ratingScaleId : '',
    }), [reupload, period, meta]);

  const ready = metaReady(meta, reupload);

  const validate = useCallback(async (chosen) => {
    const f = chosen || file;
    if (!f || !ready) return;
    setBusy(true);
    try {
      const res = await validateHistoryImport(f, requestMeta());
      if (res?.data?.status) { setPreview(res.data.data); setStale(false); }
    } catch {
      setPreview(null);   // hata tostu interceptor'da (ör. dönem adı zaten var)
    } finally {
      setBusy(false);
    }
  }, [file, ready, requestMeta]);

  const update = (patch) => {
    setMeta((m) => ({ ...m, ...patch }));
    if (preview) setStale(true);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !f.name.toLowerCase().endsWith('.xlsx')) return;
    setFile(f);
    setPreview(null);
    validate(f);
  };

  const onTemplate = async () => {
    const res = await getHistoryImportTemplate(reupload
      ? { resultKind: period.resultKind, ratingScaleId: period.ratingScaleId }
      : { resultKind: meta.resultKind, ratingScaleId: meta.resultKind === 'level' ? meta.ratingScaleId : null });
    // Hata durumunda res.data gerçek bir Blob değildir — bozuk dosya indirilmez.
    if (!(res?.data instanceof Blob) || res.data.size < 100) return;
    download(res.data, 'flexbee-gecmis-donem-sonuclari.xlsx');
  };

  const onSave = async () => {
    if (!canCommit(preview, stale)) return false;
    setConfirm(true);
    return false;   // çekmece açık kalır; kayıt onaydan sonra
  };

  const onConfirm = async () => {
    setBusy(true);
    try {
      const res = await executeHistoryImport(file, requestMeta());
      if (res?.data?.status) {
        setConfirm(false);
        onImported?.(res.data.data);
        onClose?.();
      }
    } catch {
      // Dosya bu arada değiştiyse (ör. kişi pasifleşti) sunucu hatalı satır bulur: önizleme yenilenir.
      setConfirm(false);
      await validate();
    } finally {
      setBusy(false);
    }
  };

  const items = summaryItems(preview);
  const errors = (preview?.errors || []).slice(0, ISSUE_PREVIEW_LIMIT);
  const warnings = (preview?.warnings || []).slice(0, ISSUE_PREVIEW_LIMIT);
  const issueText = (i) => `${t(`performance.historyImport.column.${i.column}`, { defaultValue: i.column })}: ${t(i.key, { defaultValue: i.key })}`;
  const kindOptions = KINDS.map((k) => ({ id: k, name: t(`performance.historyImport.kind.${k}`) }));
  const confirmMessage = reupload
    ? t('performance.historyImport.confirm.reupload', { added: preview?.added ?? 0, updated: preview?.updated ?? 0, removed: preview?.removed ?? 0 })
    : t('performance.historyImport.confirm.create', { count: preview?.validRows ?? 0 });

  return (
    <>
      <GenericFormDrawer
        open={open}
        onClose={onClose}
        title={reupload ? t('performance.historyImport.reuploadTitle', { name: period.name }) : t('performance.historyImport.newTitle')}
        schema={[]}
        submitLabel={t('performance.historyImport.import', { count: preview?.validRows ?? 0 })}
        submitDisabled={busy || !canCommit(preview, stale)}
        onSave={onSave}
      >
        <Hint>{t(reupload ? 'performance.historyImport.reuploadHint' : 'performance.historyImport.hint')}</Hint>
        {!reupload ? (
          <>
            <FieldRow>
              <TextInput label={t('performance.historyImport.field.name')} value={meta.name} required
                onChange={(e) => update({ name: e.target.value })} />
            </FieldRow>
            <FieldRow>
              <DateTimePicker label={t('performance.historyImport.field.startDate')} value={meta.startDate} required
                onChange={(v) => update({ startDate: v || '' })} />
            </FieldRow>
            <FieldRow>
              <DateTimePicker label={t('performance.historyImport.field.endDate')} value={meta.endDate} required
                onChange={(v) => update({ endDate: v || '' })} />
            </FieldRow>
            <FieldRow>
              <GenericSelect label={t('performance.historyImport.field.resultKind')} options={kindOptions} value={meta.resultKind}
                onChange={(v) => update({ resultKind: v || 'level' })} idKey="id" labelKey="name" clearable={false} required />
            </FieldRow>
            {meta.resultKind === 'level' ? (
              <FieldRow>
                <GenericSelect label={t('performance.historyImport.field.ratingScale')} options={scales} value={meta.ratingScaleId}
                  onChange={(v) => update({ ratingScaleId: v || '' })} idKey="id" labelKey="name" required />
              </FieldRow>
            ) : null}
          </>
        ) : null}

        <Section>{t('performance.historyImport.fileSection')}</Section>
        <Inline>
          <Button variant="outline" onClick={onTemplate} data-action="template">
            <Download size={16} style={{ marginRight: 6 }} />
            {t('performance.historyImport.downloadTemplate')}
          </Button>
          <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={busy || !ready} data-action="choose-file">
            <Upload size={16} style={{ marginRight: 6 }} />
            {t('performance.historyImport.chooseFile')}
          </Button>
          {file && stale ? <Button variant="ghost" onClick={() => validate()} disabled={busy || !ready} data-action="revalidate">{t('performance.historyImport.revalidate')}</Button> : null}
          <input ref={inputRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={onFile} data-testid="history-import-file" />
        </Inline>
        {!ready ? <Muted style={{ marginTop: 6 }}>{t('performance.historyImport.fillMetaFirst')}</Muted> : null}
        {file ? <Muted style={{ marginTop: 6 }} data-file="1">{file.name}</Muted> : null}

        {busy ? <Hint style={{ marginTop: 12 }}>{t('performance.historyImport.validating')}</Hint> : null}
        {preview ? (
          <>
            <Section>{t('performance.historyImport.previewSection')}</Section>
            {stale ? <Notice data-notice="stale"><AlertTriangle size={16} />{t('performance.historyImport.staleNotice')}</Notice> : null}
            {preview.errorRows > 0 ? (
              <Notice $tone="danger" data-notice="errors"><AlertTriangle size={16} />{t('performance.historyImport.errorsNotice', { count: preview.errorRows })}</Notice>
            ) : null}
            {preview.noChanges ? <Notice $tone="success" data-notice="no-changes"><CheckCircle size={16} />{t('performance.historyImport.noChangesNotice')}</Notice> : null}
            {preview.errorRows === 0 && !preview.noChanges ? (
              <Notice $tone="success" data-notice="ready"><CheckCircle size={16} />{t('performance.historyImport.readyNotice', { count: preview.validRows })}</Notice>
            ) : null}
            <Summary>
              {items.map((it) => (
                <Stat key={it.key} data-summary={it.key}>
                  <StatValue $tone={it.key === 'errorRows' && it.value > 0 ? 'danger' : undefined}>{it.value}</StatValue>
                  <StatLabel>{t(`performance.historyImport.summary.${it.key}`)}</StatLabel>
                </Stat>
              ))}
            </Summary>
            {(preview.bandDistribution || []).some((b) => b.count > 0) ? (
              <Muted data-bands="1">
                {t('performance.historyImport.bandDistribution', {
                  high: preview.bandDistribution.find((b) => b.key === 'high')?.count ?? 0,
                  mid: preview.bandDistribution.find((b) => b.key === 'mid')?.count ?? 0,
                  low: preview.bandDistribution.find((b) => b.key === 'low')?.count ?? 0,
                  none: preview.bandDistribution.find((b) => b.key === 'none')?.count ?? 0,
                })}
              </Muted>
            ) : null}
            {(preview.levelDistribution || []).length > 0 ? (
              <Muted data-levels="1" style={{ marginTop: 4 }}>
                {preview.levelDistribution.map((l) => `${l.label}: ${l.count}`).join(' · ')}
              </Muted>
            ) : null}
            {errors.length > 0 ? <Section>{t('performance.historyImport.errorsSection')}</Section> : null}
            {errors.map((i) => (
              <IssueRow key={`e-${i.row}-${i.column}-${i.key}`} $error data-issue="error" data-row={i.row}>
                <RowNo>{t('performance.historyImport.rowLabel', { row: i.row })}</RowNo>
                <span>{issueText(i)}</span>
              </IssueRow>
            ))}
            {warnings.length > 0 ? <Section>{t('performance.historyImport.warningsSection')}</Section> : null}
            {warnings.map((i) => (
              <IssueRow key={`w-${i.row}-${i.column}-${i.key}`} data-issue="warning" data-row={i.row}>
                <RowNo>{t('performance.historyImport.rowLabel', { row: i.row })}</RowNo>
                <span>{issueText(i)}</span>
              </IssueRow>
            ))}
            {preview.issuesTruncated || (preview.errors || []).length > errors.length || (preview.warnings || []).length > warnings.length
              ? <Muted data-truncated="1">{t('performance.historyImport.issuesTruncated')}</Muted> : null}
          </>
        ) : null}
      </GenericFormDrawer>
      <ConfirmDialog
        isOpen={confirm}
        type="info"
        title={t('performance.historyImport.confirm.title')}
        message={confirmMessage}
        confirmText={t('performance.historyImport.confirm.ok')}
        cancelText={t('common.cancel')}
        loading={busy}
        onConfirm={onConfirm}
        onCancel={() => setConfirm(false)}
      />
    </>
  );
}
